/**
 * Grading — ONE JOB PER (MAIN TASK × SUBMISSION), which is the call the agent was always making.
 *
 * THE UNIT USED TO BE CHOSEN BY THE LAST PHASE, NOT THE FIRST, and that is what this file's history is about. A run
 * was one main task across the whole cohort because the run ENDED with `reviewGradingConsistencyForTask`, a pass that
 * compares one task's grades across students — so the row had to hold a submission SET to have anything to compare.
 * The grading itself was never cohort-shaped: `gradeOneTask` already makes one model call per (task, submission).
 *
 * A set is the one thing a dedupe key cannot express, and most of what this file used to carry came from trying: the
 * scope resolved at RUN time (`submissionScope: 'pendingRegrade'`), the hash of a sorted id list (an index entry
 * cannot exceed 1024 bytes), and a per-wave document for the asks of dispatches that deduped away. All of that is
 * gone. What survives is `dedupeUntil: 'claimed'`, for a reason that has nothing to do with sets — a grade is
 * computed against a task's solutions as read when the row runs, so a solution replaced mid-run is an ask the
 * running row is not answering. See the declaration below.
 *
 * So the unit moves to the unit and the aggregates become rows of their own — see `agents/grading-phases.ts`:
 *
 *  - `grading` (here)      — one (main task × submission). Explicit scope, one model call, retried alone.
 *  - `gradingConsistency`  — one MAIN TASK, gated on that task having no grading row left.
 *  - `languageUseGroupGrading` — one (submission × grammar scheme GROUP), since a group spans several main tasks.
 *
 * THE THREE ARE ONE STEP (`EXTRA_STEP_TYPES`), so the wave is over only when all of them are and the announcement
 * still fires exactly once. All three are dispatched TOGETHER by `dispatchGradingWave`, which is the only thing
 * that creates any of them; this handler used to enqueue the other two itself, and the header of that module
 * records the three separate bugs that cost.
 *
 * PARALLELISM IS REAL HERE. Grades persist with `updateOne(..., { arrayFilters: [{ 'main.taskNumber': … }] })` — a
 * positional write into one element of one submission's `taskSubmissions`. The lock is therefore the SUBMISSION, the
 * same key answer extraction uses, so four hundred rows spread across the fleet instead of serialising per task.
 *
 * The exam-wide grading session tolerates this by construction: `isCurrentlyGrading` is a REFCOUNT that each run
 * increments and decrements, and `closeGradingSession` only marks the exam COMPLETED when it reaches zero AND the
 * queue holds no live grading row.
 *
 * No `desired`: whether a task needs (re-)grading is a fact about the submissions' stored grades and about what
 * changed, neither of which is derivable from the exam document. The re-grade dispatcher enqueues these.
 */
import { getCollection } from '@adornis/baseql/server/collections.js';
import { createHash } from 'node:crypto';
import { Submission } from '../../../submission.js';
import { groupTaskRefsByMainTask, prepareRegradingRun, runGradingAgent } from '../../agents/gradingAgent.js';
import type { GradeTaskTarget } from '../../agents/default-grading.js';
import {
  getTaskKey,
  hasGradingSchemeContent,
  hasMasterSolutionContent,
  isGradable,
  type NestedExamTask,
} from '../../agents/tree-generation-utils.js';
import { readySubmissionIds } from '../../../utils/exam-utils.js';
import { settling } from '../job-conditions.js';
import { anyLiveJobForTypesWhere } from '../job-store.js';
import { submissionTaskLockKey } from '../job.js';
import { gradingStep } from '../steps/grading.step.js';
import { defineJob } from '../registry.js';

export interface GradingPayload {
  /** `getTaskKey` of the main task. Identity — stable across a renumber, unlike `taskNumber`. */
  taskKey: string;
  /** What the grading agent addresses tasks BY. Carried alongside the key rather than looked up, so the row
   *  records which task number it was enqueued for even if the tree is rebuilt underneath it. */
  taskNumber: string;
  /**
   * The refs to regrade WITHIN this main task, when the request was finer than the task. Empty or absent
   * means the whole task.
   *
   * The unit of a job is the main task, but the unit of a REGRADE is not: `['3a']` means the pass runs over
   * main task 3 while only subtask a is actually regraded — `prepareRegradingRun` detects that as a
   * subtask-level request and marks only the matching nodes PENDING, and `isSubtaskFilter` in the grading
   * loop keeps the rest of the subtree untouched. Without this field the job could only ever say
   * `[taskNumber]`, which reads as "the whole main task" and silently widens every scoped regrade.
   */
  taskRefs?: string[];
  /** `content` (default) / `languageUse`. A languageUse-only regrade must not rewrite the content grades. */
  target?: GradeTaskTarget;
  /**
   * Scopes multi-member grammar-group grading to ONE LanguageUseScheme — set when the request is a save of
   * that specific scheme's own criteria/maxPoints. A task can belong to more than one group, so without
   * this, regrading a task also regrades every OTHER group it happens to share that task with, even
   * though nothing about them changed. Omitted for requests that should grade every group a task
   * belongs to (e.g. a membership change, which can affect several schemes at once).
   */
  languageUseSchemeId?: string;
  /**
   * Batches a solo (1-member) LanguageUseScheme rule into this same row's 'content' call — see
   * `GradingScope.includeSoloLanguageUse`.
   */
  includeSoloLanguageUse?: boolean;
  /**
   * WHOSE ANSWER THIS ROW GRADES — one submission, always, and this is the field the whole redesign turns on.
   *
   * It used to be a LIST, or a `submissionScope` promising to resolve one at run time, and both were ways of putting
   * a set into a dedupe key. Naming exactly one submission makes the key explicit: two requests for the same
   * (task, submission, scope) are the same work and dedupe, two requests for different submissions are different
   * work and both run. Nothing has to be resolved late, so nothing can be lost between the ask and the run.
   */
  submissionId: string;
  /**
   * Whether this row waits for the exam to SETTLE before it runs — see the gate in `ready()`.
   *
   * Set only by `dispatchRegradeIfGrading`. It is what replaced the re-grade drainer's settle window: one upload
   * routinely changes a master solution, a grading scheme and the submissions at three different times, and
   * grading on each completion produced one run and one announcement per artifact. Waiting here instead means the
   * rows coalesce (same `dedupeKey`) and the last one to be enqueued is graded once, for everything.
   *
   * PART OF THE DEDUPE KEY, and it has to be: a row the teacher's turn is waiting on must not be swallowed by a
   * queued background re-grade of the same task, nor made to wait behind a split confirmation that is sitting on
   * a human. Two rows is the correct answer there, and the second costs a repeat rather than a wrong grade.
   */
  settle?: boolean;
  [key: string]: unknown;
}

/**
 * What a caller may ask grading for. The type reads its own units from this, and every dispatch site passes it.
 *
 * Here rather than in a dispatcher module because it is this type's own contract: `batch.units` below is the only
 * thing that interprets it, and `GradingPayload` above is what it turns into.
 */
export interface GradingScope {
  /**
   * What to regrade. May be finer than a main task — `['3a']` produces ONE job for main task 3 carrying that ref, so
   * the pass still runs over the whole main task (which is what the cross-submission consistency review needs) while
   * only subtask a is regraded. Omitted grades every main task.
   */
  taskRefs?: string[];
  /** `content` (default) / `languageUse`. A languageUse-only regrade must not rewrite the content grades. */
  target?: GradeTaskTarget;
  /** Scopes multi-member grammar-group grading to ONE LanguageUseScheme — see `GradingPayload.languageUseSchemeId`. */
  languageUseSchemeId?: string;
  /**
   * Batches a solo (1-member) LanguageUseScheme rule into the same 'content' row instead of requiring a
   * separate 'languageUse' dispatch — see `GradeTaskOptions.includeSoloLanguageUse`. Only set by a caller
   * dispatching a genuine first grade of a submission (not a scoped regrade of one edited scheme).
   */
  includeSoloLanguageUse?: boolean;
  /**
   * Whose submissions. Omitted means every submission with extracted answers.
   *
   * Not a detail: several callers regrade ONE student (a teacher corrected one answer) or a named subset (applying a
   * manual grading insight to the others). Dropping this would widen those to the whole cohort — the same silent
   * widening `taskRefs` exists to prevent, one axis over.
   */
  submissionIds?: string[];
  /** Whether these rows wait for the exam to SETTLE before running — see `GradingPayload.settle`. */
  settle?: boolean;
  /**
   * The main tasks whose grades this wave will reconcile — read only by `gradingConsistency`'s `units`.
   *
   * Set by `dispatchGradingWave` from the payloads `grading` just enumerated, so the three phases of one wave
   * agree on the task set by construction rather than by two independent enumerations that could drift.
   *
   * `taskRefs` travels with each entry for the same reason it is on a `grading` row: the reconciliation is
   * scoped to whatever the wave actually graded, and the canvas reads it off the row to know which boxes to
   * show as busy. Without it a subtask-scoped regrade would light its siblings up again the moment this phase
   * started.
   */
  consistencyTasks?: Array<{ taskKey: string; taskNumber: string; taskRefs?: string[] }>;
}

/**
 * A scope's stable signature, for the dedupe key. Sorted, so ordering cannot make two identical rows.
 *
 * STILL HASHED, but no longer because it has to be: the submission list is gone, so what is left is a target, a
 * flag and the refs within one main task. `taskRefs` is the only unbounded part and a deep subtree could still
 * push a raw key past the 1024-byte limit Mongo enforces on an index entry, which is a failed insert rather than a
 * degraded one. The cost is that a key cannot be read back — see `scopeNote`, which puts the readable form on the
 * row beside it.
 */
const scopeKey = (payload: GradingPayload): string => {
  const scope = scopeNote(payload);
  return createHash('sha1').update(scope).digest('hex').slice(0, 16);
};

/** The same scope, readable — so "what was this row for?" is answerable from the row without reversing a hash. */
const scopeNote = (payload: GradingPayload): string =>
  [
    payload.target ?? 'content',
    payload.settle ? 'settle' : 'now',
    [...(payload.taskRefs ?? [])].sort().join(','),
    // A scheme-scoped request and a full regrade of the same task refs are different work — see
    // GradingPayload.languageUseSchemeId — so this must be part of the key or one would dedupe onto the other.
    payload.languageUseSchemeId ?? '',
    // A combined first-grade row and a strictly-scoped content-only regrade of the same task refs are also
    // different work — see GradingPayload.includeSoloLanguageUse — same reason.
    payload.includeSoloLanguageUse ? 'soloLanguageUse' : '',
  ].join('|');

/**
 * Whose answers this DISPATCH covers: the named subset when the caller scoped to one, everything ready otherwise.
 *
 * RESOLVED AT ENUMERATION, not in the handler, and that relocation is the point. It used to run inside the row,
 * because the row stood for a set that was still growing — so a submission arriving after the read was announced as
 * graded without being graded. One row per submission means the set is only ever expanded by ENQUEUEING, and an
 * enqueue cannot be lost: it either inserts a row or collides with a row for that exact submission.
 */
async function submissionsForScope(examId: string, scope: GradingScope): Promise<string[]> {
  if (scope.submissionIds?.length) return scope.submissionIds.map(String);
  return readySubmissionIds(examId);
}

/** Whether the whole subtree under a main task has what grading needs. Mirrors `isTaskReadyForGrading`. */
const subtreeGradable = (node: NestedExamTask): boolean => {
  if (isGradable(node) && (!hasMasterSolutionContent(node) || !hasGradingSchemeContent(node))) return false;
  const subTasks = Array.isArray((node as any).subTasks) ? ((node as any).subTasks as NestedExamTask[]) : [];
  return subTasks.every(subtreeGradable);
};

export const gradingJob = defineJob<GradingPayload>({
  type: 'grading',

  /**
   * Closes grading when this exam has no `grading` row left. No run to supply, join or derive.
   *
   * NO `progress`, and that omission is the declaration: `grading` is not a `SetupStep`, keeps no progress record of
   * the kind `updateSetupStep` announces, and closes its own session instead. `finish` is where a step says "this is
   * what my completion means" when it is not a setup step at all — see `StepCompletion`.
   */
  onStepComplete: gradingStep,

  /**
   * ONE ROW PER MAIN TASK, whatever the scope asked for — the granularity the agent's own structure dictates, and the
   * reason this grouping exists at all. Grading used to be dispatched per SUBMISSION, and that is the fan-out
   * inside out: `runGradingAgent`'s outer loop is over TASKS and its inner loop over submissions, because each task
   * ends with `reviewGradingConsistencyForTask` — a pass comparing one task's grades ACROSS students. Fanning out per
   * submission ran that pass once per student over a single student's grades, which is not a comparison.
   *
   * GROUPED BY MAIN TASK rather than matched against it: a subtask-level request like `['3a']` names no main task, so
   * matching `taskRefs` directly would dispatch nothing. `groupTaskRefsByMainTask` maps each ref to its root and keeps
   * the refs that selected it, which is what lets the pass run over main task 3 while only subtask a is re-graded.
   *
   * NO RUN TO OPEN: grading's progress is a session refcount that each run increments, not a per-unit set to seed.
   */
  batch: {
    units: async (examId, scope) => {
      const typed = scope as GradingScope;
      const { taskRefs, target, settle, languageUseSchemeId, includeSoloLanguageUse } = typed;
      const groups = await groupTaskRefsByMainTask(examId, taskRefs);
      const submissionIds = await submissionsForScope(examId, typed);
      // THE CARTESIAN PRODUCT, enumerated. Three tasks and forty submissions is a hundred and twenty rows, and that
      // is the intended shape: `dispatch.ts` already argues why a round of four hundred is four hundred rows rather
      // than one row carrying four hundred items — every guarantee this system has (`lockKey` exclusion, the dedupe
      // key, the lease, the retry count) is a unique index on a DOCUMENT, and none of them can be lifted down to an
      // element of a subdocument array.
      return groups.flatMap(group =>
        submissionIds.map(submissionId => ({
          taskKey: group.taskKey,
          taskNumber: group.taskNumber,
          submissionId,
          ...(group.refs.length > 0 ? { taskRefs: group.refs } : {}),
          ...(target ? { target } : {}),
          ...(settle ? { settle: true } : {}),
          ...(languageUseSchemeId ? { languageUseSchemeId } : {}),
          ...(includeSoloLanguageUse ? { includeSoloLanguageUse: true } : {}),
        })),
      );
    },
    joinTwin: true,
    /** Generous: a batch is every task in scope against every submission, and overrunning costs an announcement. */
    waitMs: 30 * 60_000,
  },

  /**
   * Per (SUBMISSION × MAIN TASK), which is exactly what this row writes: a positional update into one element of
   * one submission's `taskSubmissions`. Never `exam.tasks`, so grading also runs alongside a master-solution
   * regeneration rather than queueing behind it.
   *
   * IT USED TO BE THE WHOLE SUBMISSION, and that was the conservative choice rather than a required one — the
   * comment here said as much: "they write disjoint array elements and need not". What it cost was every
   * parallelism within one student: task 1 graded, finished, then task 2, then the language-use group, because
   * all three took the same key. A single submission's exam therefore ran strictly serially no matter how much
   * headroom the runner had.
   *
   * Two things had to be true before it could be narrowed, and both now are. The writes are addressed rather
   * than read-modify-write (`prepareRegradingRun`'s subtask path was the last one that rebuilt the array whole).
   * And the whole-document writer this used to be serialised against — answer extraction, which `$set`s the
   * entire `taskSubmissions` — is excluded by the gate in `ready()` instead, since it no longer shares this key.
   */
  lockKey: (_examId, payload) => submissionTaskLockKey(payload.submissionId, payload.taskKey),

  /**
   * Includes the SCOPE, not just the task. A grammar-only regrade of `3a` and a full regrade of task 3 are
   * different work, and keying on the task alone would let whichever was queued first swallow the other.
   * The reverse — a wide regrade making a narrow queued one redundant — is a merge rather than a dedupe, and
   * running the narrow one afterwards costs a repeat, not a wrong grade.
   */
  dedupeKey: (examId, payload) => `grading:${examId}:${payload.taskKey}:${payload.submissionId}:${scopeKey(payload)}`,

  /**
   * ONLY UNTIL CLAIMED — and for a narrower reason than it used to be.
   *
   * It used to be here because a row's SCOPE was resolved at run time: it read an accumulator to discover which
   * submissions to grade, so a submission arriving after that read was never graded and still announced. That is
   * gone — a row names its submission in the payload.
   *
   * WHAT REMAINS IS THE INPUT. A grade is computed against this task's master solution and grading scheme, read
   * from the tree when the row RUNS and deliberately not carried in the payload (the tree can be rebuilt between
   * enqueue and run, which is why the row carries `taskKey` rather than a copy of the content). So a grading-scheme
   * upload that lands while a row for that task is running is asking for something the running row is not doing —
   * and deduping onto it would drop that ask silently while the wave announced the new scheme as applied.
   *
   * Releasing at the claim splits the two cases by status, which is exactly the split that is correct:
   *  - a PENDING twin absorbs, and the joining trigger's cause is pushed onto it (`JobDoc.intents`). It has read
   *    nothing yet, so one row does the work of both asks and the announcement naming both is true.
   *  - a CLAIMED twin does not, so the second ask gets its own row. `lockKey` is per (submission, task) — never
   *    finer than this key, which is the invariant `submissionTaskLockKey` documents — so two twins still hold
   *    the same lock, are still serialised, and the later one genuinely reads the later tree.
   */
  dedupeUntil: 'claimed',

  ready: async (exam, job) => {
    const root = ((exam.tasks ?? []) as unknown as NestedExamTask[]).find(
      task => getTaskKey(task) === job.payload.taskKey,
    );
    if (!root) return { verdict: 'drop', because: 'grading:task-gone' };
    // The reactive wait that `runGradingAgent` currently implements with a poll loop
    // (`GRADING_WAIT_POLL_MS`), expressed as a gate instead: this task grades as soon as ITS master solution
    // and grading scheme are present, whatever the rest of the exam is still doing.
    if (!subtreeGradable(root)) return { verdict: 'wait', because: 'grading:solutions-incomplete' };

    // THE SETTLE WINDOW, as a gate instead of a scheduler — see `GradingPayload.settle` and `settling`, which
    // every phase of the wave shares so they cannot wait out different windows.
    if (settling(exam, job)) return { verdict: 'wait', because: 'grading:exam-not-settled' };

    // ANSWER EXTRACTION FOR THIS SUBMISSION MUST NOT BE IN FLIGHT, and this gate is what replaced the lock that
    // used to guarantee it. Extraction holds `submission:<id>` and `$set`s the whole `taskSubmissions` array
    // (`process-submission.ts`); grading held the same key, so the two could never overlap. Now that grading
    // locks per (submission, task) they can, and the array write would land on top of — or underneath — grades
    // for answers it is in the middle of replacing.
    //
    // ONE-SIDED DELIBERATELY. `anyLiveJobForTypesWhere` counts `pending` as live, so gating both directions
    // would livelock: a pending extraction row and a pending grading row would each see the other and neither
    // would ever claim. Gating only here cannot, because the ungated side always reaches a terminal status and
    // opens this gate on its way. The residual is an extraction enqueued after a grading row already claimed,
    // which this cannot hold — the common ordering is safe anyway, since extraction's own handler dispatches
    // the re-grade only once it has finished.
    //
    // Last of the checks, and asked of the QUEUE rather than the exam, so the cheap pure ones decide first and
    // this single indexed lookup runs only for a row that would otherwise start (see `processExam`).
    if (await anyLiveJobForTypesWhere(job.examId, ['answerExtraction'], { submissionId: job.payload.submissionId })) {
      return { verdict: 'wait', because: 'grading:answers-extracting' };
    }

    return 'run';
  },

  /**
   * ONE SUBMISSION'S ONE TASK — a couple of model calls, not a cohort, and the number had to come down with the
   * unit. It was thirty minutes because a row was a whole task across every student.
   *
   * Leaving it there would not have been merely generous: a claimed row is only reclaimable at
   * `deadlineAt + LEASE_GRACE_MS`, so after an ungraceful death every one of a cohort's rows would have sat
   * unreclaimable for half an hour. That is the cost `maxRuntimeMs` exists to let a type avoid paying.
   */
  maxRuntimeMs: 6 * 60_000,
  maxAttempts: 2,

  handler: async (examId, payload) => {
    const { submissionId } = payload;
    // The requested refs when the regrade was scoped finer than the task, the task itself otherwise. Passing
    // `[taskNumber]` unconditionally would tell `prepareRegradingRun` this is a main-task request and widen
    // a subtask regrade to the whole subtree.
    const taskRefs = payload.taskRefs?.length ? payload.taskRefs : [payload.taskNumber];

    // Re-checked after the claim rather than in `ready()`, because it is a fact about the SUBMISSION and `ready`
    // only ever sees the exam. A submission deleted or reset between the enqueue and the run is a skip, not a
    // failure — there is nothing for a retry to fix.
    const submissionCollection = await getCollection(Submission);
    const submission = await submissionCollection.findOne({ _id: submissionId }, { projection: { _id: 1 } });
    if (!submission) return { skipped: true, note: 'submission no longer exists' };

    // Resetting this scope's grading states to PENDING belongs to the job, not to whoever enqueued it: it has
    // to happen again on a requeue, and every converted caller used to do it immediately before its own
    // `runGradingAgent` call. Scoped exactly like the grading below, so a subtask regrade only re-pends that
    // subtask (`isSubtaskLevelRequest`).
    await prepareRegradingRun(examId, [submissionId], taskRefs);

    // ONE (TASK, SUBMISSION) PAIR. The run does phase one only — it cannot review consistency (one student, nothing
    // to compare) and must not grade a language-use group (a group spans main tasks, so it would be graded once per
    // member task).
    //
    // IT NO LONGER ENQUEUES THE OTHER TWO PHASES EITHER. They used to be inserted from here, while this row was
    // still `claimed`, so that the step could not drain between them; `dispatchGradingWave` now creates all three
    // up front instead, which is strictly safer and buys two things this could not. A follow-up insert is only
    // ever deduped by a row that is still LIVE, so as soon as a group row could finish before a sibling content
    // row — which is exactly what the narrower per-task lock allows — this would have inserted a second row and
    // graded the group twice. And a phase that does not exist until the first content row finishes cannot be
    // waited on by the caller, nor shown as pending by the canvas.
    const outcome = await runGradingAgent(
      examId,
      [submissionId],
      taskRefs,
      payload.target ?? 'content',
      payload.includeSoloLanguageUse ?? false,
    );

    return {
      note: `graded ${taskRefs.join(', ')} for submission ${submissionId} (${scopeNote(payload)})`,
      // WHAT THIS ROW DID, for the wave to aggregate. `gradedSubmissionIds` is what the announcement counts;
      // `consistencyItems` is what the per-task reconciliation reads, and it is the reason `JobDoc.result` exists —
      // the write coordinates for an adjustment are derived while the grade is being built and cannot be rebuilt
      // from the stored document without duplicating that derivation.
      result: {
        gradedSubmissionIds: [submissionId],
        gradedTaskRefs: taskRefs,
        consistencyItems: outcome.consistencyItems,
      },
    };
  },
});
